import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { money, money2, useAsync } from "../lib";
import { Empty, Skeleton, LoginGate } from "../ui";
import { WITHDRAW_STATUS } from "./Withdraw";

// เมนูลัด — ไอคอนชุด Fluent Emoji แบบ 3 มิติ (Iconify "fluent-emoji") + สีประจำเมนู (hue) ใช้เป็นพื้นเรืองของไทล์
const FEATURES = [
  { key: "promo", to: "/promotion", icon: "fluent-emoji:wrapped-gift", hue: "#fb7185", label: "โปรโมชั่น", caption: "รับโบนัสฝาก" },
  { key: "wheel", to: "/wheel", icon: "fluent-emoji:slot-machine", hue: "#a78bfa", label: "กงล้อ", caption: "ลุ้นเครดิตฟรี" },
  { key: "checkin", to: "/checkin", icon: "fluent-emoji:calendar", hue: "#38bdf8", label: "เช็คอิน", caption: "รับทุกวัน" },
  { key: "refer", to: "/refer", icon: "fluent-emoji:handshake", hue: "#34d399", label: "แนะนำเพื่อน", caption: "รับค่าคอม" },
  { key: "creditcode", to: "/creditcode", icon: "fluent-emoji:ticket", hue: "#fbbf24", label: "เครดิตโค้ด", caption: "กรอกรหัสรับฟรี" },
  { key: "ranking", to: "/ranking", icon: "fluent-emoji:trophy", hue: "#f59e0b", label: "แรงค์", caption: "เลื่อนระดับรับโบนัส" },
  { key: "cashback", to: "/cashback", icon: "fluent-emoji:umbrella-with-rain-drops", hue: "#60a5fa", label: "คืนยอดเสีย", caption: "ร่มกันฝนของคุณ" },
  { key: "activity", to: "/activity", icon: "fluent-emoji:megaphone", hue: "#f472b6", label: "กิจกรรม", caption: "อ่าน ดู ตอบ รับรางวัล" },
  { key: "reviews", to: "/reviews", icon: "fluent-emoji:glowing-star", hue: "#facc15", label: "รีวิวลูกค้า", caption: "เสียงจากสมาชิก" },
];

// topupType ในระบบมีหลายรหัส (MANUALDEPOSIT, FIXMANUALDEPOSIT, CASHDEPOSIT, WITHDRAW_REVIEW ...)
// ลูกค้าไม่ต้องเห็นรายละเอียดภายใน — ยุบเป็นคำไทยกลุ่มเดียว จับด้วย keyword เพื่อครอบคลุมรหัสใหม่ด้วย
// kind = กลุ่มสี/ไอคอน/ตัวกรอง (ดู KINDS)
const LEDGER_RULES = [
  [/REFUND/, "คืนเงิน", "refund"],
  [/WITHDRAW/, "ถอนเงิน", "out"],
  [/DEPOSIT|TOPUP|PAYIN|CASHTOCREDIT/, "เติมเงิน", "in"],
  [/CREDITFREE/, "เครดิตฟรี", "bonus"],
  [/CREDITCODE/, "โค้ดเครดิต", "bonus"],
  [/CASHBACK/, "คืนยอดเสีย", "bonus"],
  [/COMMISSION|REVSHARE/, "ค่าคอมมิชชั่น", "bonus"],
  [/BONUS/, "โบนัส", "bonus"],
  [/WHEEL/, "กงล้อ", "bonus"],
  [/CHECKIN/, "เช็คอิน", "bonus"],
  [/ACTIVITY/, "รางวัลกิจกรรม", "bonus"],
  [/PAYOUT|PRIZE|WIN/, "ถูกรางวัล", "win"],
  [/BET/, "แทงหวย", "bet"],
  [/ADJUST/, "ปรับยอด", "other"],
];

// sign: +1 เงินเข้า, -1 เงินออก, 0 = ดูจากเครื่องหมายของยอด
const KINDS = {
  in: { icon: "solar:download-minimalistic-bold", sign: 1, tab: "in" },
  out: { icon: "solar:upload-minimalistic-bold", sign: -1, tab: "out" },
  bonus: { icon: "solar:gift-bold-duotone", sign: 1, tab: "other" },
  win: { icon: "solar:cup-star-bold-duotone", sign: 1, tab: "other" },
  refund: { icon: "solar:restart-bold", sign: 1, tab: "other" },
  bet: { icon: "solar:ticket-bold-duotone", sign: -1, tab: "other" },
  other: { icon: "solar:document-text-bold-duotone", sign: 0, tab: "other" },
};

const TABS = [
  { key: "all", label: "ทั้งหมด" },
  { key: "in", label: "เติมเงิน" },
  { key: "out", label: "ถอนเงิน" },
  { key: "other", label: "โบนัส/อื่นๆ" },
];

const PAGE = 12;
const HIDE_KEY = "lx_hide_balance";

function ledgerKind(raw, isIn) {
  const s = String(raw || "").trim();
  const hit = LEDGER_RULES.find(([re]) => re.test(s.toUpperCase()));
  if (hit) return { label: hit[1], kind: hit[2] };
  // ข้อความที่เป็นภาษาคนอยู่แล้ว (ไม่ใช่รหัส A-Z_) แสดงตามเดิม
  if (s && !/^[A-Z0-9_]+$/.test(s)) return { label: s, kind: "other" };
  return isIn ? { label: "เติมเงิน", kind: "in" } : { label: "ถอนเงิน", kind: "out" };
}

// created_at มาได้ทั้ง ISO (มี Z) และ "YYYY-MM-DD HH:mm:ss" (เวลาไทย) — แบบหลังให้อ่านเป็นเวลาเครื่อง
function toDate(v) {
  if (!v) return null;
  const d = new Date(typeof v === "string" ? v.replace(" ", "T") : v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayLabel(d) {
  if (!d) return "ไม่ระบุวันที่";
  const today = new Date();
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "วันนี้";
  if (d.toDateString() === y.toDateString()) return "เมื่อวาน";
  return d.toLocaleDateString("th-TH", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

const timeLabel = (d) =>
  d ? `${d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })} น.` : "";

function fromTopup(row, i) {
  const amt = Number(row.amount ?? row.approveAmount ?? 0);
  const { label, kind } = ledgerKind(
    row.type || row.topupType || row.transactionType || row.messageText,
    amt >= 0,
  );
  const sign = amt < 0 ? -1 : KINDS[kind].sign || 1;
  const bonus = Number(row.BonusAmount) || 0;
  const note = row.PromotionDetail
    ? `โปร ${row.PromotionDetail}${bonus > 0 ? ` · โบนัส +${money(bonus)}` : ""}`
    : bonus > 0
      ? `รับโบนัส +${money(bonus)}`
      : "";
  const after = row.afterBalance != null && row.afterBalance !== "" ? Number(row.afterBalance) : null;
  return {
    id: `t${row.topupHistoryID ?? row.id ?? i}`,
    at: toDate(row.created_at),
    label,
    kind,
    sign,
    amount: Math.abs(amt),
    note,
    after: Number.isFinite(after) ? after : null,
  };
}

// ประวัติถอนอยู่คนละตาราง (withdrawRequest) — รวมเข้ามาให้ผู้ใช้เห็นเงินเข้า-ออกครบในที่เดียว
function fromWithdraw(row, i) {
  return {
    id: `w${row.withdrawRequestID ?? i}`,
    at: toDate(row.created_at),
    label: "ถอนเงิน",
    kind: "out",
    sign: -1,
    amount: Math.abs(Number(row.approveAmount || row.amount) || 0),
    note: row.bankname ? `เข้าบัญชี ${row.bankname}` : "",
    status: WITHDRAW_STATUS[row.status] || (row.status ? { t: row.status, c: "lose" } : null),
    voided: row.status === "DISAPPROVED",
    after: null,
  };
}

const readHide = () => {
  try {
    return window.localStorage.getItem(HIDE_KEY) === "1";
  } catch {
    return false;
  }
};

export default function AccountPage({ balance, onRefresh }) {
  const { user, isAuthenticated, logout } = useAuth();
  const nav = useNavigate();
  const uid = isAuthenticated ? user?.id : null;
  const [tab, setTab] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const [hidden, setHidden] = useState(readHide);

  const {
    data: entries,
    loading,
    reload,
  } = useAsync(async () => {
    if (!uid) return [];
    // ถอนล่มไม่ควรทำให้รายการฝากหายทั้งหมด (และกลับกัน)
    const [tops, wds] = await Promise.all([
      lottoApi.ledger(uid, { limit: 30 }).catch(() => []),
      lottoApi.withdrawHistory(uid).catch(() => []),
    ]);
    const list = [
      ...(Array.isArray(tops) ? tops : []).map(fromTopup),
      ...(Array.isArray(wds) ? wds : []).map(fromWithdraw),
    ];
    return list.sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0));
  }, [uid]);

  const { data: feat } = useAsync(() => lottoApi.features().catch(() => null), []);
  // ค่าเริ่มต้น = แสดงทุกอย่าง (เผื่อ API ล่ม) — ปิดเฉพาะเมื่อแอดมินตั้ง flag = 0
  const show = (k) => !feat || feat[k] !== false;
  const features = FEATURES.filter((f) => show(f.key));

  const counts = useMemo(() => {
    const c = { all: 0, in: 0, out: 0, other: 0 };
    (entries || []).forEach((e) => {
      c.all += 1;
      c[KINDS[e.kind].tab] += 1;
    });
    return c;
  }, [entries]);

  // ยอดเข้า-ออกของวันนี้ (จากรายการที่โหลดมา; รายการที่ถูกปฏิเสธไม่นับ)
  const today = useMemo(() => {
    const t = { in: 0, out: 0 };
    const now = new Date().toDateString();
    (entries || []).forEach((e) => {
      if (e.voided || !e.at || e.at.toDateString() !== now) return;
      if (e.sign > 0) t.in += e.amount;
      else t.out += e.amount;
    });
    return t;
  }, [entries]);

  const filtered = useMemo(
    () => (entries || []).filter((e) => tab === "all" || KINDS[e.kind].tab === tab),
    [entries, tab],
  );

  // แบ่งกลุ่มตามวัน (รายการเรียงใหม่→เก่าอยู่แล้ว)
  const groups = useMemo(() => {
    const out = [];
    filtered.slice(0, limit).forEach((e) => {
      const label = dayLabel(e.at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(e);
      else out.push({ label, items: [e] });
    });
    return out;
  }, [filtered, limit]);

  const toggleHidden = () => {
    setHidden((h) => {
      try {
        window.localStorage.setItem(HIDE_KEY, h ? "0" : "1");
      } catch {
        /* private mode — จำไม่ได้ก็ไม่เป็นไร */
      }
      return !h;
    });
  };

  const refresh = () => {
    reload();
    if (onRefresh) onRefresh();
  };

  if (!isAuthenticated)
    return (
      <LoginGate
        icon="fluent-emoji-flat:money-bag"
        title="เข้าสู่ระบบเพื่อดูกระเป๋าเงิน"
        desc="ดูยอดเงินและรายการเดินบัญชีล่าสุดของคุณได้ทุกเมื่อ"
        perks={["ดูยอดเงินคงเหลือและรายการเดินบัญชี","ฝาก-ถอนได้จากที่เดียว","เข้าถึงโปรโมชั่น เช็คอิน และกงล้อ"]}
        onLogin={() => nav("/auth")}
      />
    );

  const displayName = user?.book_name || user?.name || user?.username || "สมาชิก";
  const initial = String(displayName).trim().charAt(0).toUpperCase() || "U";

  return (
    <div className="vx-acc">
      {/* ---- กระเป๋าเงิน: บัตรสมาชิก ---- */}
      <section className="vx-wcard" aria-label="กระเป๋าเงิน">
        <div className="vx-wcard-deco" aria-hidden="true">
          <Icon icon="fluent-emoji:coin" width={64} height={64} className="vx-wcard-coin c1" />
          <Icon icon="fluent-emoji:coin" width={38} height={38} className="vx-wcard-coin c2" />
          <Icon icon="fluent-emoji:sparkles" width={34} height={34} className="vx-wcard-coin c3" />
        </div>
        <div className="vx-wcard-main">
          <div className="vx-wcard-top">
            <span className="lx-acc-avatar vx-wcard-avatar" aria-hidden="true">
              {initial}
            </span>
            <div className="vx-wcard-who">
              <div className="vx-wcard-name">{displayName}</div>
              {user?.username && <div className="vx-wcard-uid">ยูสเซอร์ {user.username}</div>}
            </div>
            <button
              type="button"
              className={`vx-wcard-ico ${loading ? "spin" : ""}`}
              onClick={refresh}
              title="อัปเดตยอดเงิน"
              aria-label="อัปเดตยอดเงิน"
            >
              <Icon icon="solar:refresh-bold" width={18} height={18} />
            </button>
          </div>

          <div className="vx-wcard-mid">
            <div className="vx-wcard-label">
              <Icon icon="fluent-emoji-flat:coin" width={18} height={18} />
              ยอดเงินคงเหลือ
              <button
                type="button"
                className="vx-wcard-eye"
                onClick={toggleHidden}
                aria-label={hidden ? "แสดงยอดเงิน" : "ซ่อนยอดเงิน"}
                title={hidden ? "แสดงยอดเงิน" : "ซ่อนยอดเงิน"}
              >
                <Icon icon={hidden ? "solar:eye-closed-bold" : "solar:eye-bold"} width={16} height={16} />
              </button>
            </div>
            <div className="vx-wcard-bal">
              <span className="lx-mono">{hidden ? "••••••" : money2(balance)}</span>
              <small>บาท</small>
            </div>
            {entries && !hidden && (
              <div className="vx-wcard-today" aria-label="สรุปวันนี้">
                <span className="in">
                  <Icon icon="solar:round-arrow-down-bold" width={15} height={15} /> เข้าวันนี้ <b className="lx-mono">+{money2(today.in)}</b>
                </span>
                <span className="out">
                  <Icon icon="solar:round-arrow-up-bold" width={15} height={15} /> ออกวันนี้ <b className="lx-mono">−{money2(today.out)}</b>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="vx-wcard-actions">
          <button type="button" className="vx-wcard-act main" onClick={() => nav("/deposit")}>
            <Icon icon="solar:download-minimalistic-bold" width={20} height={20} />
            ฝากเงิน
          </button>
          <button type="button" className="vx-wcard-act" onClick={() => nav("/withdraw")}>
            <Icon icon="solar:upload-minimalistic-bold" width={20} height={20} />
            ถอนเงิน
          </button>
        </div>
      </section>

      {/* ---- เมนูลัด ---- */}
      <section className="vx-acc-menu">
        {!!features.length && (
          <>
            <h2 className="vx-acc-h">
              <Icon icon="fluent-emoji:sparkles" width={22} height={22} /> เมนูลัด
            </h2>
            <nav className="vx-ftiles" aria-label="เมนูลัด">
              {features.map((f) => (
                <button key={f.key} type="button" className="vx-ftile" style={{ "--fh": f.hue }} onClick={() => nav(f.to)}>
                  <span className="vx-ftile-ic" aria-hidden="true">
                    <Icon icon={f.icon} width={46} height={46} />
                  </span>
                  <span className="vx-ftile-tx">
                    <b>{f.label}</b>
                    <small>{f.caption}</small>
                  </span>
                </button>
              ))}
            </nav>
          </>
        )}
        <button
          type="button"
          className="lx-acc-logout"
          onClick={() => {
            if (window.confirm("ต้องการออกจากระบบใช่ไหม?")) logout();
          }}
        >
          <Icon icon="solar:logout-2-bold" width={18} height={18} />
          ออกจากระบบ
        </button>
      </section>

      {/* ---- รายการเดินบัญชี ---- */}
      <section className="lx-acc-ledger">
        <div className="lx-acc-ledger-head">
          <h2 className="lx-h2">รายการเดินบัญชีล่าสุด</h2>
          <button type="button" className="lx-acc-link" onClick={refresh} disabled={loading}>
            <Icon icon="solar:refresh-bold" width={14} height={14} />
            รีเฟรช
          </button>
        </div>

        <div className="lx-seg lx-acc-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={tab === t.key ? "on" : ""}
              onClick={() => {
                setTab(t.key);
                setLimit(PAGE);
              }}
            >
              {t.label}
              {!loading && <span className="lx-acc-count">{counts[t.key]}</span>}
            </button>
          ))}
        </div>

        {loading && !entries && (
          <div className="lx-card lx-acc-list">
            {[0, 1, 2, 3].map((k) => (
              <Skeleton key={k} h={46} style={{ margin: "6px 0" }} />
            ))}
          </div>
        )}

        {!loading && !filtered.length && (
          <div className="lx-card">
            <Empty icon="solar:bill-list-bold-duotone">
              {tab === "all" ? "ยังไม่มีรายการเดินบัญชี" : "ไม่มีรายการในหมวดนี้"}
              {tab === "all" && (
                <div style={{ marginTop: 12 }}>
                  <button className="lx-btn primary" onClick={() => nav("/deposit")}>
                    ฝากเงินครั้งแรก
                  </button>
                </div>
              )}
            </Empty>
          </div>
        )}

        {groups.map((g) => (
          <div key={g.label} className="lx-acc-group">
            <div className="lx-acc-day">{g.label}</div>
            <div className="lx-card lx-acc-list">
              {g.items.map((e) => {
                const isIn = e.sign > 0;
                return (
                  <div key={e.id} className={`lx-acc-row k-${e.kind} ${e.voided ? "voided" : ""}`}>
                    <span className="lx-acc-row-ic" aria-hidden="true">
                      <Icon icon={KINDS[e.kind].icon} width={20} height={20} />
                    </span>
                    <div className="lx-acc-row-main">
                      <div className="lx-acc-row-title">
                        {e.label}
                        {e.status && <span className={`lx-tag ${e.status.c}`}>{e.status.t}</span>}
                      </div>
                      <div className="lx-acc-row-sub">
                        {timeLabel(e.at)}
                        {e.note && ` · ${e.note}`}
                      </div>
                    </div>
                    <div className="lx-acc-row-end">
                      <b className={`lx-mono lx-acc-amt ${isIn ? "in" : "out"}`}>
                        {isIn ? "+" : "−"}
                        {money2(e.amount)}
                      </b>
                      {e.after != null && !hidden && (
                        <small className="lx-mono">คงเหลือ {money2(e.after)}</small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {filtered.length > limit && (
          <button type="button" className="lx-btn ghost block lx-acc-more" onClick={() => setLimit((n) => n + PAGE)}>
            ดูเพิ่มอีก {Math.min(PAGE, filtered.length - limit)} รายการ
          </button>
        )}
      </section>
    </div>
  );
}
